import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { supabase } from '../../../../../data/supabase';
import { useAuth } from '../../../../../shared/context/useAuth';
import { useCreditData } from '../../../../../shared/hooks/useCreditData';
import toast from 'react-hot-toast';

export default function MyPackagesPage() {
  const { currentUser } = useAuth();
  const [patientId, setPatientId] = useState(null);
  const [activeTab, setActiveTab] = useState('packages');
  
  // Buscar ID do paciente
  useEffect(() => {
    const fetchPatientId = async () => {
      if (!currentUser?.id) return;
      
      try {
        const { data: patient, error } = await supabase
          .from('patient')
          .select('id')
          .eq('id_user', currentUser.id)
          .single();
        
        if (error) throw error;
        setPatientId(patient.id);
      } catch (error) {
        console.error('Erro ao buscar paciente:', error);
      }
    };
    
    fetchPatientId();
  }, [currentUser?.id]);

  // Usar o hook de dados de crédito
  const {
    packages,
    wallets,
    transactions,
    loading,
    error,
    refetch,
    calculateBalance,
    getAvailableSessionTypes
  } = useCreditData(patientId);

  // Função para formatar o tipo de crédito/pacote
  const formatCreditType = (type) => {
    console.log('type', type);
    
    if (!type) return 'Individual';
    
    switch(type) {
      case 'individual':
        return 'Individual';
      case 'casal':
        return 'Casal';
      case 'familia':
        return 'Família';
      case 'package':
        return 'Pacote';
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  // Função para obter a classe de cor baseada no tipo
  const getTypeColorClass = (type) => {
    if (!type) return 'border-[#3BBED0]';
    
    switch(type) {
      case 'individual':
        return 'border-blue-500';
      case 'casal':
        return 'border-purple-500';
      case 'familia':
        return 'border-green-500';
      case 'package':
        return 'border-orange-500';
      default:
        return 'border-[#3BBED0]';
    }
  };

  // Função para obter o badge de tipo com cor
  const getTypeBadge = (type) => {
    let bgColor = '';
    let textColor = 'text-white';
    
    if (!type) {
      bgColor = 'bg-[#3BBED0]';
      return (
        <span className={`${bgColor} ${textColor} text-xs px-2 py-1 rounded-full`}>
          Individual
        </span>
      );
    }
    
    switch(type) {
      case 'individual':
        bgColor = 'bg-blue-500';
        break;
      case 'casal':
        bgColor = 'bg-purple-500';
        break;
      case 'familia':
        bgColor = 'bg-green-500';
        break;
      case 'package':
        bgColor = 'bg-orange-500';
        break;
      default:
        bgColor = 'bg-gray-500';
    }
    
    return (
      <span className={`${bgColor} ${textColor} text-xs px-2 py-1 rounded-full`}>
        {formatCreditType(type)}
      </span>
    );
  };

  // Função para obter o ícone baseado no tipo
  const getTypeIcon = (type) => {
    if (!type) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#3BBED0]" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
        </svg>
      );
    }
    
    switch(type) {
      case 'individual':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
          </svg>
        );
      case 'casal':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-500" viewBox="0 0 20 20" fill="currentColor">
            <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
          </svg>
        );
      case 'familia':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
            <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
          </svg>
        );
      case 'package':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-orange-500" viewBox="0 0 20 20" fill="currentColor">
            <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
            <path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  // Função para formatar o tipo de transação
  const formatTransactionType = (type) => {
    switch(type) {
      case 'purchase':
        return 'Compra';
      case 'appointment':
        return 'Agendamento';
      case 'cancellation':
        return 'Cancelamento';
      case 'expiration':
        return 'Expiração';
      case 'refund':
        return 'Reembolso';
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  // Função para obter a classe de cor baseada no tipo de transação
  const getTransactionColorClass = (type, amount) => {
    if (amount > 0) {
      return 'text-green-600';
    } else {
      return 'text-red-600';
    }
  };

  const handleRefresh = async () => {
    await refetch();
    toast.success('Dados atualizados com sucesso!');
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 flex justify-center items-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3BBED0]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6 text-center">
        <h1 className="text-2xl font-bold mb-4 text-red-500">Erro</h1>
        <p className="mb-4">{error}</p>
        <div className="flex flex-col sm:flex-row justify-center gap-3">
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-[#3BBED0] text-white rounded hover:bg-[#2AA9BD]"
          >
            Tentar Novamente
          </button>
          <Link
            to="/pagamentos"
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            Ver Pacotes Disponíveis
          </Link>
        </div>
      </div>
    );
  }

  const hasNoData = packages.length === 0 && transactions.length === 0;
  const availableSessionTypes = getAvailableSessionTypes();
  const hasPackageTransactions = transactions.some(t => t.source_type === 'package');
  const hasIndividualTransactions = transactions.some(t => t.source_type === 'individual_credit');

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Meus Pacotes e Sessões</h1>
        <button
          onClick={handleRefresh}
          className="px-3 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 flex items-center"
          disabled={loading}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Atualizar
        </button>
      </div>
      
      {hasNoData ? (
        <div className="bg-gray-100 p-6 rounded-lg text-center">
          <h2 className="text-lg font-semibold mb-2">Você ainda não possui pacotes ou créditos</h2>
          <p className="mb-4">Adquira um pacote de terapia para economizar ou compre créditos individuais.</p>
          <Link
            to="/pagamentos"
            className="px-4 py-2 bg-[#3BBED0] text-white rounded hover:bg-[#2AA9BD]"
          >
            Ver Opções Disponíveis
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Tabs para alternar entre pacotes e créditos */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('packages')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'packages'
                    ? 'border-[#3BBED0] text-[#3BBED0]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Pacotes
              </button>
              <button
                onClick={() => setActiveTab('credits')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'credits'
                    ? 'border-[#3BBED0] text-[#3BBED0]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Créditos Individuais
              </button>
            </nav>
          </div>
          
          {/* Conteúdo dos pacotes */}
          {activeTab === 'packages' && (
            <>
              {/* Pacotes na Carteira */}
              {hasPackageTransactions ? (
                <div className="mt-8">
                  <h2 className="text-xl font-semibold mb-4">Créditos de Pacotes na Carteira</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {availableSessionTypes.map((sessionType, index) => {
                      const packageCredits = calculateBalance(sessionType, 'package');
                      
                      // Não exibir se não houver créditos deste tipo
                      if (packageCredits.total === 0) {
                        return null;
                      }
                      
                      return (
                        <div 
                          key={index} 
                          className={`bg-white rounded-lg shadow p-4 border-l-4 ${getTypeColorClass(sessionType)}`}
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex items-center">
                              {getTypeIcon(sessionType)}
                              <h3 className="font-bold text-lg ml-2">
                                Pacote {formatCreditType(sessionType)}
                              </h3>
                            </div>
                            {getTypeBadge(sessionType)}
                          </div>
                          
                          <div className="mt-2 space-y-1">
                            <p className="flex justify-between">
                              <span>Créditos disponíveis:</span>
                              <span className="font-semibold">{packageCredits.available}</span>
                            </p>
                            <p className="flex justify-between">
                              <span>Créditos utilizados:</span>
                              <span>{packageCredits.used}</span>
                            </p>
                            <p className="text-sm text-gray-500 mt-2">
                              {packageCredits.total} créditos adquiridos no total
                            </p>
                          </div>
                          
                          {packageCredits.available > 0 && (
                            <div className="mt-3">
                              <Link 
                                to="/consultas" 
                                className="inline-block px-4 py-2 bg-[#3BBED0] text-white rounded hover:bg-[#2AA9BD] text-sm"
                              >
                                Agendar Sessão {formatCreditType(sessionType)}
                              </Link>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="bg-gray-100 p-6 rounded-lg text-center mt-4">
                  <h3 className="text-lg font-semibold mb-2">Você não possui pacotes ativos</h3>
                  <p className="mb-4">Adquira um pacote de terapia para economizar.</p>
                  <Link
                    to="/pagamentos"
                    className="px-4 py-2 bg-[#3BBED0] text-white rounded hover:bg-[#2AA9BD]"
                  >
                    Ver Pacotes Disponíveis
                  </Link>
                </div>
              )}
              
              {/* Histórico de transações de pacotes */}
              <div className="mt-8">
                <h2 className="text-xl font-semibold mb-4">Histórico de Transações de Pacotes</h2>
                
                {transactions.filter(t => t.source_type === 'package').length === 0 ? (
                  <p className="text-gray-500 italic">Nenhuma transação de pacote registrada até o momento.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white">
                      <thead>
                        <tr className="bg-gray-100 text-gray-600 uppercase text-sm leading-normal">
                          <th className="py-3 px-6 text-left">Data</th>
                          <th className="py-3 px-6 text-left">Tipo</th>
                          <th className="py-3 px-6 text-left">Descrição</th>
                          <th className="py-3 px-6 text-right">Quantidade</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-600 text-sm">
                        {transactions
                          .filter(t => t.source_type === 'package')
                          .map((transaction, idx) => {
                            // Determinar o tipo de sessão
                            let sessionType = transaction.wallet?.type;
                            if (!sessionType || sessionType === 'package') {
                              // Tentar extrair o tipo das notas
                              if (transaction.notes) {
                                const lowerNotes = transaction.notes.toLowerCase();
                                if (lowerNotes.includes('individual')) sessionType = 'individual';
                                else if (lowerNotes.includes('casal')) sessionType = 'casal';
                                else if (lowerNotes.includes('família') || lowerNotes.includes('familia')) sessionType = 'familia';
                                else sessionType = 'individual'; // Padrão
                              } else {
                                sessionType = 'individual'; // Padrão
                              }
                            }
                            
                            return (
                              <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                                <td className="py-3 px-6 text-left">
                                  {moment(transaction.created_at).format('DD/MM/YYYY HH:mm')}
                                </td>
                                <td className="py-3 px-6 text-left">
                                  <div className="flex items-center">
                                    {getTypeIcon(sessionType)}
                                    <span className="ml-2">{formatCreditType(sessionType)}</span>
                                  </div>
                                </td>
                                <td className="py-3 px-6 text-left">
                                  {formatTransactionType(transaction.transaction_type)}
                                  {transaction.appointment && ` - Consulta #${transaction.appointment.id.substring(0, 8)}`}
                                  {transaction.notes && ` - ${transaction.notes}`}
                                </td>
                                <td className={`py-3 px-6 text-right font-medium ${getTransactionColorClass(transaction.transaction_type, transaction.amount)}`}>
                                  {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
          
          {/* Conteúdo dos créditos individuais */}
          {activeTab === 'credits' && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Meus Créditos Individuais</h2>
              
              {!hasIndividualTransactions ? (
                <div className="bg-gray-100 p-6 rounded-lg text-center">
                  <h3 className="text-lg font-semibold mb-2">Você não possui créditos individuais para sessão</h3>
                  <p className="mb-4">Adquira créditos para agendar sessões avulsas.</p>
                  <Link
                    to="/pagamentos"
                    className="px-4 py-2 bg-[#3BBED0] text-white rounded hover:bg-[#2AA9BD]"
                  >
                    Comprar Créditos
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {availableSessionTypes.map((sessionType, index) => {
                    const individualCredits = calculateBalance(sessionType, 'individual_credit');
                    
                    // Não exibir se não houver créditos deste tipo
                    if (individualCredits.total === 0) {
                      return null;
                    }
                    
                    return (
                      <div 
                        key={index} 
                        className={`bg-white rounded-lg shadow p-4 border-l-4 ${getTypeColorClass(sessionType)}`}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex items-center">
                            {getTypeIcon(sessionType)}
                            <h3 className="font-bold text-lg ml-2">
                              Crédito {formatCreditType(sessionType)}
                            </h3>
                          </div>
                          {getTypeBadge(sessionType)}
                        </div>
                        
                        <div className="mt-2 space-y-1">
                          <p className="flex justify-between">
                            <span>Créditos disponíveis:</span>
                            <span className="font-semibold">{individualCredits.available}</span>
                          </p>
                          <p className="flex justify-between">
                            <span>Créditos utilizados:</span>
                            <span>{individualCredits.used}</span>
                          </p>
                          <p className="text-sm text-gray-500 mt-2">
                            {individualCredits.total} créditos adquiridos no total
                          </p>
                        </div>
                        
                        {individualCredits.available > 0 && (
                          <div className="mt-3">
                            <Link 
                              to="/consultas" 
                              className="inline-block px-4 py-2 bg-[#3BBED0] text-white rounded hover:bg-[#2AA9BD] text-sm"
                            >
                              Agendar Sessão {formatCreditType(sessionType)}
                            </Link>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Histórico de transações de créditos individuais */}
              <div className="mt-8">
                <h2 className="text-xl font-semibold mb-4">Histórico de Transações de Créditos Individuais</h2>
                
                {transactions.filter(t => t.source_type === 'individual_credit').length === 0 ? (
                  <p className="text-gray-500 italic">Nenhuma transação de crédito individual registrada até o momento.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white">
                      <thead>
                        <tr className="bg-gray-100 text-gray-600 uppercase text-sm leading-normal">
                          <th className="py-3 px-6 text-left">Data</th>
                          <th className="py-3 px-6 text-left">Tipo</th>
                          <th className="py-3 px-6 text-left">Descrição</th>
                          <th className="py-3 px-6 text-right">Quantidade</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-600 text-sm">
                        {transactions
                          .filter(t => t.source_type === 'individual_credit')
                          .map((transaction, idx) => {
                            // Determinar o tipo de sessão
                            let sessionType = transaction.wallet?.type;
                            if (!sessionType || sessionType === 'individual') {
                              // Tentar extrair o tipo das notas
                              if (transaction.notes) {
                                const lowerNotes = transaction.notes.toLowerCase();
                                if (lowerNotes.includes('individual')) sessionType = 'individual';
                                else if (lowerNotes.includes('casal')) sessionType = 'casal';
                                else if (lowerNotes.includes('família') || lowerNotes.includes('familia')) sessionType = 'familia';
                                else sessionType = 'individual'; // Padrão
                              } else {
                                sessionType = 'individual'; // Padrão
                              }
                            }
                            
                            return (
                              <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                                <td className="py-3 px-6 text-left">
                                  {moment(transaction.created_at).format('DD/MM/YYYY HH:mm')}
                                </td>
                                <td className="py-3 px-6 text-left">
                                  <div className="flex items-center">
                                    {getTypeIcon(sessionType)}
                                    <span className="ml-2">{formatCreditType(sessionType)}</span>
                                  </div>
                                </td>
                                <td className="py-3 px-6 text-left">
                                  {formatTransactionType(transaction.transaction_type)}
                                  {transaction.appointment && ` - Consulta #${transaction.appointment.id.substring(0, 8)}`}
                                  {transaction.notes && ` - ${transaction.notes}`}
                                </td>
                                <td className={`py-3 px-6 text-right font-medium ${getTransactionColorClass(transaction.transaction_type, transaction.amount)}`}>
                                  {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="mt-6 text-center">
            <Link
              to="/pagamentos"
              className="px-4 py-2 bg-[#3BBED0] text-white rounded hover:bg-[#2AA9BD]"
            >
              Adquirir Novos Pacotes ou Créditos
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}